import { ArrowRight } from 'lucide-react'
import { ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { companyFacts, leadershipTeam, type TeamMember } from '@/data/about'
import { HALFTONE_STYLE, Highlight } from './Highlight'

/** Same rotation as the reference's colour backdrops: blue, pink, purple, then shifted. */
const BACKDROPS = [
  'var(--color-landing-accent)',
  '#ff00f2',
  'var(--color-landing-secondary)',
  '#ff00f2',
  'var(--color-landing-secondary)',
  'var(--color-landing-accent)',
] as const

const initialsOf = (name: string) =>
  name
    .replace(/^(Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, '')
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()

function MonogramTile({ member, index }: { member: TeamMember; index: number }) {
  return (
    <figure className="group">
      <div
        aria-hidden
        className="relative aspect-[3/4] overflow-hidden [container-type:inline-size]"
        style={{ background: BACKDROPS[index % BACKDROPS.length] }}
      >
        <div
          className="absolute inset-0 opacity-60 transition-opacity duration-300 ease-out [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
          style={HALFTONE_STYLE}
        />
        <span className="absolute bottom-[5%] left-[7%] font-[family-name:var(--font-landing-display)] text-[38cqw] uppercase leading-none tracking-[-0.04em] text-white">
          {initialsOf(member.name)}
        </span>
      </div>
      <figcaption className="mt-4">
        <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase leading-tight tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
          {member.name}
        </h3>
        <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)] md:text-base">
          {member.role}
        </p>
      </figcaption>
    </figure>
  )
}

/**
 * Team section (.4fr / 1fr): sticky heading column with a quote, company
 * facts and a link to the open roles; a three-column grid of monogram tiles
 * on solid brand backdrops (no photography).
 */
export function TeamSection() {
  return (
    <section aria-labelledby="team-title" className="landing-section">
      <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
        <div className="lg:sticky lg:top-28">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
            <span id="team-title">
              Meet the <Highlight color="purple">team.</Highlight>
            </span>
          </TextReveal>
          <ScrollReveal direction="none" delay={0.15}>
            <p className="mt-8 max-w-[34ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
              The people building Templatean: tools that amplify professional expertise instead of slowing it down.
            </p>
            <dl className="mt-10 grid max-w-sm grid-cols-2 gap-6">
              {companyFacts.map((fact) => (
                <div key={fact.label}>
                  <dt className="text-sm text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                    {fact.label}
                  </dt>
                  <dd className="mt-1 font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase text-[var(--color-landing-gray-900)] dark:text-white">
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
            <a
              href="#careers"
              className="group mt-10 inline-flex items-center gap-3 font-[family-name:var(--font-landing-display)] text-lg uppercase text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:text-white"
            >
              Join the team
              <ArrowRight aria-hidden className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
          </ScrollReveal>
        </div>

        <StaggerContainer className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 md:gap-y-16" amount={0.05}>
          {leadershipTeam.map((member, index) => (
            <StaggerItem key={member.name}>
              <MonogramTile member={member} index={index} />
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  )
}
